<script setup lang="ts">
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { ref, computed, watch, onMounted, onUnmounted, createApp } from "vue";
import vuetify from "@/plugins/vuetify";
import FlightRefineMapPopup from "./FlightRefineMapPopup.vue";
import {
	productContacts,
	sumMaxAvailableImpressions,
} from "./priceQuoteMetrics";
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import responseProductDTO = Modules.CampaignPlanner.Data.PriceQuoteApi.responseProductDTO;

const props = defineProps<{
	flight: FlightDO;
	products: responseProductDTO[];
	readonly?: boolean;
}>();

const emit = defineEmits<{
	"toggle-product": [product: responseProductDTO];
}>();

const mapContainer = ref<HTMLDivElement>();
const map = ref<mapboxgl.Map>();

const DUTCH_CENTER: mapboxgl.LngLatLike = { lng: 5.2913, lat: 52.1326 };
const DEFAULT_ZOOM = 7;

const formatReach = (n: number): string => {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
	return String(Math.round(n));
};

const createSelectedEl = (reach: number): HTMLElement => {
	const r = 12;
	const sw = 3;
	const size = (r + sw / 2 + 2) * 2;
	const cx = size / 2;
	const cy = size / 2;
	const label = formatReach(reach);
	const fontSize = label.length <= 3 ? 10 : 8;

	const el = document.createElement("div");
	el.style.cssText = `width:${size}px;height:${size}px;cursor:pointer;`;
	el.innerHTML = `
		<div style="width:100%;height:100%;">
			<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
				<circle cx="${cx}" cy="${cy}" r="${r}" fill="#fcfcfd"/>
				<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#2563eb" stroke-width="${sw}"/>
				<text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central"
					font-family="'Open Sans Semibold', sans-serif" font-size="${fontSize}" fill="#2563eb">${label}</text>
			</svg>
		</div>`;
	return el;
};

const createDeselectedEl = (): HTMLElement => {
	const r = 10;
	const sw = 2;
	const size = (r + sw / 2 + 2) * 2;
	const cx = size / 2;
	const cy = size / 2;

	const el = document.createElement("div");
	el.style.cssText = `width:${size}px;height:${size}px;cursor:pointer;`;
	el.innerHTML = `
		<div style="opacity:0.35;width:100%;height:100%;">
			<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
				<circle cx="${cx}" cy="${cy}" r="${r}" fill="#fcfcfd"/>
				<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#9ca3af" stroke-width="${sw}"/>
			</svg>
		</div>`;
	return el;
};

const createSelectedClusterEl = (count: number, reachSum: number): HTMLElement => {
	const ringR = Math.round(
		count <= 25
			? 14 + (count / 25) * 10
			: 24 + Math.min((count - 25) / 75, 1) * 10
	);
	const sw = 3;
	const size = (ringR + sw / 2 + 2) * 2;
	const cx = size / 2;
	const cy = size / 2;
	const label = formatReach(reachSum);
	const fontSize = label.length <= 3 ? (ringR < 18 ? 11 : 14) : ringR < 18 ? 9 : 12;

	const el = document.createElement("div");
	el.style.cssText = `width:${size}px;height:${size}px;cursor:pointer;`;
	el.innerHTML = `
		<div style="width:100%;height:100%;">
			<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
				<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="#fcfcfd"/>
				<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="none" stroke="#2563eb" stroke-width="${sw}"/>
				<text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central"
					font-family="'Open Sans Semibold', sans-serif" font-size="${fontSize}" fill="#2563eb">${label}</text>
			</svg>
		</div>`;
	return el;
};

const createDeselectedClusterEl = (count: number): HTMLElement => {
	const ringR = Math.round(
		count <= 25
			? 14 + (count / 25) * 10
			: 24 + Math.min((count - 25) / 75, 1) * 10
	);
	const sw = 2;
	const size = (ringR + sw / 2 + 2) * 2;
	const cx = size / 2;
	const cy = size / 2;
	const fontSize = ringR < 18 ? 11 : ringR < 24 ? 14 : 18;

	const el = document.createElement("div");
	el.style.cssText = `width:${size}px;height:${size}px;cursor:pointer;`;
	el.innerHTML = `
		<div style="opacity:0.35;width:100%;height:100%;">
			<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
				<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="#fcfcfd"/>
				<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="none" stroke="#9ca3af" stroke-width="${sw}"/>
				<text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central"
					font-family="'Open Sans Semibold', sans-serif" font-size="${fontSize}" fill="#9ca3af">${count}</text>
			</svg>
		</div>`;
	return el;
};

const selectedProducts = computed(() =>
	props.products.filter(
		(p) => p.isSelected && p.screen?.address_data?.lat && p.screen?.address_data?.long
	)
);

const deselectedProducts = computed(() =>
	props.products.filter(
		(p) => !p.isSelected && p.screen?.address_data?.lat && p.screen?.address_data?.long
	)
);

// Denominator for the goal share must match DetailViewRefineSegments' kpis:
// the sum over ALL selected products in the segment, not just the geocoded ones
// that get a marker (selectedProducts is filtered by lat/long).
const selectedMaxAvailableImpressions = computed(() =>
	sumMaxAvailableImpressions(props.products.filter((p) => p.isSelected))
);

/**
 * Reach shown on a marker. In the summary view (readonly) products arrive with
 * `pqa_audience` already aggregated across segments, so use it directly. While
 * editing a segment we derive the contact metric from the live selection.
 */
const productReach = (p: responseProductDTO): number =>
	props.readonly
		? p.pqa_audience ?? 0
		: productContacts(props.flight, p, selectedMaxAvailableImpressions.value);

const selectedGeoJSON = computed<GeoJSON.GeoJSON>(() => ({
	type: "FeatureCollection",
	features: selectedProducts.value.map((p) => ({
		type: "Feature" as const,
		properties: {
			reference_id: p.reference_id ?? "",
			reach: productReach(p),
		},
		geometry: {
			type: "Point" as const,
			coordinates: [p.screen!.address_data!.long!, p.screen!.address_data!.lat!],
		},
	})),
}));

const deselectedGeoJSON = computed<GeoJSON.GeoJSON>(() => ({
	type: "FeatureCollection",
	features: deselectedProducts.value.map((p) => ({
		type: "Feature" as const,
		properties: {
			reference_id: p.reference_id ?? "",
		},
		geometry: {
			type: "Point" as const,
			coordinates: [p.screen!.address_data!.long!, p.screen!.address_data!.lat!],
		},
	})),
}));

const resizeObserver = new ResizeObserver(() => map.value?.resize());
const markers: mapboxgl.Marker[] = [];
const activePopup = ref<mapboxgl.Popup | null>(null);

const handleOutsideClick = (e: MouseEvent) => {
	if (
		activePopup.value &&
		mapContainer.value &&
		!mapContainer.value.contains(e.target as Node)
	) {
		activePopup.value.remove();
	}
};

const showPopup = (
	referenceId: string | undefined,
	clusterId: number | undefined,
	sourceId: string,
	coordinates: [number, number]
) => {
	if (props.readonly) return;
	activePopup.value?.remove();

	const allProducts =
		sourceId === "selected-products"
			? selectedProducts.value
			: deselectedProducts.value;

	const div = document.createElement("div");
	let popup: mapboxgl.Popup;
	const app = createApp(FlightRefineMapPopup, {
		clusterId,
		map: clusterId !== undefined ? map.value : undefined,
		sourceId: clusterId !== undefined ? sourceId : undefined,
		coordinates,
		allProducts,
		singleReferenceId: referenceId,
		getReach: productReach,
		getPopup: () => popup,
		onToggle: (product: responseProductDTO) => {
			emit("toggle-product", product);
		},
	}).use(vuetify);
	app.mount(div);

	popup = new mapboxgl.Popup({
		closeButton: false,
		offset: 14,
		maxWidth: "none",
	})
		.setLngLat(coordinates)
		.setDOMContent(div)
		.addTo(map.value!);

	popup.on("close", () => {
		app.unmount();
		activePopup.value = null;
	});

	activePopup.value = popup;
};

const refreshMarkers = () => {
	markers.forEach((m) => m.remove());
	markers.length = 0;

	if (!map.value) return;

	// Deselected individual
	const deselectedIndividual = map.value.querySourceFeatures("deselected-products", {
		filter: ["!", ["has", "point_count"]],
	});
	const seenDeselected = new Set<string>();
	for (const feature of deselectedIndividual) {
		const refId = feature.properties?.reference_id as string;
		if (seenDeselected.has(refId)) continue;
		seenDeselected.add(refId);
		const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
		const el = createDeselectedEl();
		el.addEventListener("click", (e) => {
			e.stopPropagation();
			showPopup(refId, undefined, "deselected-products", [lng, lat]);
		});
		markers.push(
			new mapboxgl.Marker({ element: el, anchor: "center" })
				.setLngLat([lng, lat])
				.addTo(map.value!)
		);
	}

	// Deselected clusters
	const deselectedClusters = map.value.querySourceFeatures("deselected-products", {
		filter: ["has", "point_count"],
	});
	const seenDeselectedClusters = new Set<number>();
	for (const feature of deselectedClusters) {
		const clusterId = feature.properties?.cluster_id as number;
		if (seenDeselectedClusters.has(clusterId)) continue;
		seenDeselectedClusters.add(clusterId);
		const count = feature.properties?.point_count as number;
		const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
		const el = createDeselectedClusterEl(count);
		el.addEventListener("click", (e) => {
			e.stopPropagation();
			showPopup(undefined, clusterId, "deselected-products", [lng, lat]);
		});
		markers.push(
			new mapboxgl.Marker({ element: el, anchor: "center" })
				.setLngLat([lng, lat])
				.addTo(map.value!)
		);
	}

	// Selected individual
	const selectedIndividual = map.value.querySourceFeatures("selected-products", {
		filter: ["!", ["has", "point_count"]],
	});
	const seenSelected = new Set<string>();
	for (const feature of selectedIndividual) {
		const refId = feature.properties?.reference_id as string;
		if (seenSelected.has(refId)) continue;
		seenSelected.add(refId);
		const reach = Number(feature.properties?.reach) || 0;
		const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
		const el = createSelectedEl(reach);
		el.addEventListener("click", (e) => {
			e.stopPropagation();
			showPopup(refId, undefined, "selected-products", [lng, lat]);
		});
		markers.push(
			new mapboxgl.Marker({ element: el, anchor: "center" })
				.setLngLat([lng, lat])
				.addTo(map.value!)
		);
	}

	// Selected clusters
	const selectedClusters = map.value.querySourceFeatures("selected-products", {
		filter: ["has", "point_count"],
	});
	const seenSelectedClusters = new Set<number>();
	for (const feature of selectedClusters) {
		const clusterId = feature.properties?.cluster_id as number;
		if (seenSelectedClusters.has(clusterId)) continue;
		seenSelectedClusters.add(clusterId);
		const count = feature.properties?.point_count as number;
		const reachSum = Number(feature.properties?.reachSum) || 0;
		const [lng, lat] = (feature.geometry as GeoJSON.Point).coordinates;
		const el = createSelectedClusterEl(count, reachSum);
		el.addEventListener("click", (e) => {
			e.stopPropagation();
			showPopup(undefined, clusterId, "selected-products", [lng, lat]);
		});
		markers.push(
			new mapboxgl.Marker({ element: el, anchor: "center" })
				.setLngLat([lng, lat])
				.addTo(map.value!)
		);
	}
};

onMounted(() => {
	mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN ?? "";

	map.value = new mapboxgl.Map({
		container: mapContainer.value!,
		style: "mapbox://styles/mapbox/light-v11",
		center: DUTCH_CENTER,
		zoom: DEFAULT_ZOOM,
		fadeDuration: 0,
	});

	resizeObserver.observe(mapContainer.value!);
	document.addEventListener("click", handleOutsideClick);

	map.value.on("load", () => {
		map.value!.addSource("deselected-products", {
			type: "geojson",
			data: deselectedGeoJSON.value,
			cluster: true,
			clusterRadius: 50,
			clusterMaxZoom: 14,
		});
		map.value!.addLayer({
			id: "deselected-tile-loader",
			type: "circle",
			source: "deselected-products",
			paint: { "circle-radius": 0, "circle-opacity": 0 },
		});

		map.value!.addSource("selected-products", {
			type: "geojson",
			data: selectedGeoJSON.value,
			cluster: true,
			clusterRadius: 50,
			clusterMaxZoom: 14,
			clusterProperties: {
				reachSum: ["+", ["coalesce", ["get", "reach"], 0]],
			},
		});
		map.value!.addLayer({
			id: "selected-tile-loader",
			type: "circle",
			source: "selected-products",
			paint: { "circle-radius": 0, "circle-opacity": 0 },
		});

		map.value!.on("moveend", refreshMarkers);
		map.value!.once("idle", refreshMarkers);
	});
});

watch(selectedGeoJSON, (geoJSON) => {
	const source = map.value?.getSource("selected-products") as
		| mapboxgl.GeoJSONSource
		| undefined;
	source?.setData(geoJSON);
	map.value?.once("idle", refreshMarkers);
});

watch(deselectedGeoJSON, (geoJSON) => {
	const source = map.value?.getSource("deselected-products") as
		| mapboxgl.GeoJSONSource
		| undefined;
	source?.setData(geoJSON);
	map.value?.once("idle", refreshMarkers);
});

onUnmounted(() => {
	markers.forEach((m) => m.remove());
	resizeObserver.disconnect();
	document.removeEventListener("click", handleOutsideClick);
	map.value?.remove();
});
</script>

<template>
	<div ref="mapContainer" class="map-container" />
</template>

<style scoped lang="scss">
.map-container {
	width: 100%;
	height: 100%;
}

:deep(.mapboxgl-ctrl) {
	display: none !important;
}
</style>
